import { Link, useLocation } from 'react-router-dom';
import escapadeLogoMug from '../../images/escapadeLogoMug.png';
import escapadeLogoMugGray from '../../images/escapadeLogoMugGray.png';
import escapadeLogoWordDM from '../../images/escapadeLogoWordDM.png';
import escapadeLogoWordLM from '../../images/escapadeLogoWordLM.png';
import { SECTION_HOME, VIEW_HOME } from '../../common/EsConstants';
import { useEsServerDown } from '../../common/EsServerStatus';
import { useEsStrings } from '../../common/EsStrings';
import './EsNavLogo.css';

/**
 * The nav bar's logo, built as the footer's is: the mug and the wordmark as two
 * images at fixed heights, so the bar is the mug's 36px -- the account button's
 * height, esNavDisc (Tim, 2026-10-11) -- plus py-3, a little air above and
 * below it and the round controls, at every width.
 * While esback is unreachable the mug is swapped for the gray one, pulsing dim;
 * the wordmark never changes.
 *
 * Below sm the wordmark shrinks to whatever the row leaves it. A phone's bar also
 * holds up to four round controls, the hamburger, and a label whose length is its
 * locale's, and a 156px wordmark beside them overflows the row. The mug carries
 * the name, so the link is "Escapade" at every width.
 */
export default function EsNavLogo() {
    const t = useEsStrings();
    const down = useEsServerDown();
    const { pathname, hash } = useLocation();

    // Already on the hero, a Link to the same url changes nothing EsScrollToTop
    // watches (pathname and hash), so the scroll the native anchor used to do is
    // done here.
    const onLogo = () => {
        if ( pathname === VIEW_HOME && hash === SECTION_HOME ) window.scrollTo( { top: 0, behavior: 'smooth' } );
    };

    return (
        // A Link, not <a href> (Tim, 2026-10-22): the href was a full page load,
        // which remounted AuthProvider -- authenticated false, loading true until
        // /authUser answered -- and drew the logged-out bar for a beat on every click.
        // Client-side, the provider is never remounted and auth stays known.
        <Link
            to={ VIEW_HOME + SECTION_HOME }
            onClick={ onLogo }
            data-testid="es-nav-logo"
            data-tip={ down ? t( 'nav-logo-server-down', 'The server is not reachable' ) : undefined }
            // The mug and the wordmark at every width (Tim, 2026-10-12): a phone-wide row
            // squished the mug and hid the word, and neither is what the logo is for.
            // Below sm the wordmark scales DOWN to the room the row leaves it instead, max-h
            // and max-w with no fixed height, so it keeps its aspect ratio. The room is the
            // flex row's to give, not a measured cap: a cap is the rest of one locale's bar,
            // and the labels beside it are each locale's length (locale sweep, 2026-10-19).
            // From sm the logo never shrinks.
            className="flex min-w-0 shrink items-center gap-2 py-4 sm:shrink-0 sm:gap-4"
        >
            { /* es-copy: literal -- the name is the brand in every language */ }
            <img src={ down ? escapadeLogoMugGray : escapadeLogoMug } alt="Escapade" className={ 'h-9 w-auto shrink-0' + ( down ? ' navLogoDown' : '' ) } />
            <span className="flex min-w-0">
                <img src={ escapadeLogoWordLM } alt="" className="max-h-8 w-auto max-w-full sm:max-w-none dark:hidden" />
                <img src={ escapadeLogoWordDM } alt="" className="hidden max-h-8 w-auto max-w-full sm:max-w-none dark:block" />
            </span>
        </Link>
    );
}
